import { use, useCallback, useMemo } from 'react';
import { Checkbox, colors } from 'component-library';
import { DropdownContexts } from 'component-library/components/Input/Dropdown/context/DropdownContext';
import { type UserPickerDropdownItem } from '../types';
import {
  UserItemWrapper,
  UserItemContent,
  UserName,
  UserEmailRow,
  UserEmail,
  UserBadge,
} from './styles';

type UserListItemProps = {
  item: UserPickerDropdownItem;
  maxSelections?: number;
  selectedCount: number;
};

export const UserListItem = ({ item, maxSelections, selectedCount }: UserListItemProps) => {
  const { toggleItem, isItemSelected } = use(DropdownContexts.useValues);

  const checked = useMemo(() => isItemSelected(item) === true, [isItemSelected, item]);

  const isUnselectable = item.meta.isUnselectable === true;

  const isDisabled = useMemo(() => {
    if (isUnselectable) return false;

    if (item.disabled) return true;

    if (maxSelections !== undefined && !checked && selectedCount >= maxSelections) return true;

    return false;
  }, [item.disabled, isUnselectable, maxSelections, checked, selectedCount]);

  const handleClick = useCallback(() => {
    if (isDisabled || isUnselectable) return;
    toggleItem(item);
  }, [isDisabled, isUnselectable, toggleItem, item]);

  const badge = item.meta.badge;

  return (
    <UserItemWrapper
      onClick={handleClick}
      disabled={isDisabled}
      locked={isUnselectable}
      data-testid={`userlistitem-${item.label.toUpperCase()}`}
    >
      <Checkbox
        onChange={handleClick}
        checked={checked || isUnselectable}
        color={colors.primary.grey}
        disabled={isDisabled || isUnselectable}
      />
      <UserItemContent>
        <UserName>{item.label}</UserName>
        <UserEmailRow>
          {badge && <UserBadge variant={badge.variant}>{badge.text}</UserBadge>}
          {item.meta.email && <UserEmail>{item.meta.email}</UserEmail>}
        </UserEmailRow>
      </UserItemContent>
    </UserItemWrapper>
  );
};
